import { ArrowRightToLine } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import { PANEL_HEADER_HEIGHT_CLASS } from '@/shared/config';
import { ViewersPresence, type ViewerPresenceItem } from '@/shared/ui/primitives';

const COPILOT_TABS = [
  { id: 'copilot', label: 'Cortex' },
  { id: 'notepad', label: 'Internal Notes' },
] as const;

interface CopilotHeaderProps {
  /** Currently active tab */
  activeTab?: string;
  /** Callback when a tab is selected */
  onTabChange?: (tabId: string) => void;
  /** Whether the panel is collapsed */
  isCollapsed?: boolean;
  /** Toggle collapse/expand */
  onToggleCollapsed?: () => void;
  /** Active clinician viewers for this patient */
  viewers?: ReadonlyArray<ViewerPresenceItem>;
  /** Extra action buttons rendered before the collapse toggle */
  extraActions?: React.ReactNode;
}

/**
 * CopilotHeader — header row for the copilot panel.
 *
 * Composes:
 * - HeaderTabs (left)
 * - Collapse/expand toggle (right)
 */
export function CopilotHeader({
  activeTab = 'copilot',
  onTabChange,
  isCollapsed = false,
  onToggleCollapsed,
  viewers,
  extraActions,
}: CopilotHeaderProps) {
  return (
    <div className={`flex ${PANEL_HEADER_HEIGHT_CLASS} items-center justify-between px-2`}>
      <Tabs value={activeTab} onValueChange={(tabId) => onTabChange?.(tabId)}>
        <TabsList aria-label="Copilot views">
          {COPILOT_TABS.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="flex items-center gap-2">
        {viewers !== undefined && viewers.length > 0 ? <ViewersPresence viewers={viewers} /> : null}
        {extraActions}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggleCollapsed}
          aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
        >
          <ArrowRightToLine className={isCollapsed ? 'h-4 w-4 rotate-180' : 'h-4 w-4'} />
        </Button>
      </div>
    </div>
  );
}
